import { useState, useRef, useEffect } from 'react';
import { 
  Bot, 
  X, 
  Send, 
  Minimize2, 
  Maximize2, 
  Sparkles,
  TrendingUp,
  Shield,
  Zap,
  Loader2
} from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../utils/api';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface AIAssistantProps {
  initialOpen?: boolean;
}

export function AIAssistant({ initialOpen = false }: AIAssistantProps) {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [showQuickActions, setShowQuickActions] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Check AI service status
  const { data: aiStatus } = useQuery({
    queryKey: ['ai-status'],
    queryFn: async () => {
      const response = await api.get('/ai/status');
      return response.data;
    },
    refetchInterval: 30000,
  });

  // Chat mutation
  const chatMutation = useMutation({
    mutationFn: async (message: string) => {
      const response = await api.post('/ai/chat', {
        message,
        history: messages.slice(-10).map(m => ({
          role: m.role,
          content: m.content,
        })),
      });
      return response.data;
    },
    onSuccess: (data) => {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.message,
        timestamp: new Date(),
      }]);
    },
    onError: () => {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm having trouble connecting. Please try again in a moment.",
        timestamp: new Date(),
      }]);
    },
  });

  // Get strategy suggestions
  const strategiesMutation = useMutation({
    mutationFn: async (pair: string) => {
      const response = await api.post('/ai/strategies', {
        pair,
        currentPrice: 95000,
        priceChange24h: 2.5,
        volume24h: 50000000000,
        high24h: 96000,
        low24h: 93000,
      });
      return response.data;
    },
  });

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setShowQuickActions(false);
    chatMutation.mutate(input);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickActions = [
    {
      icon: TrendingUp,
      label: 'Strategy Suggestions',
      action: () => {
        setMessages(prev => [...prev, {
          role: 'user',
          content: 'What trading strategies would you recommend for BTC right now?',
          timestamp: new Date(),
        }]);
        setShowQuickActions(false);
        chatMutation.mutate('What trading strategies would you recommend for BTC right now?');
      },
    },
    {
      icon: Shield,
      label: 'Risk Assessment',
      action: () => {
        setMessages(prev => [...prev, {
          role: 'user',
          content: 'Can you help me assess the risk of my current trade?',
          timestamp: new Date(),
        }]);
        setShowQuickActions(false);
        chatMutation.mutate('Can you help me assess the risk of my current trade?');
      },
    },
    {
      icon: Zap,
      label: 'Build Workflow',
      action: () => {
        setMessages(prev => [...prev, {
          role: 'user',
          content: 'Help me create an automated trading workflow',
          timestamp: new Date(),
        }]);
        setShowQuickActions(false);
        chatMutation.mutate('Help me create an automated trading workflow');
      },
    },
  ];

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-primary to-cyan-400 rounded-full shadow-lg shadow-primary/30 flex items-center justify-center hover:scale-110 transition-all duration-300 z-50 group"
      >
        <Bot className="w-7 h-7 text-black" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-dark animate-pulse" />
        <span className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
      </button>
    );
  }

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${
        isMinimized ? 'w-72' : 'w-96'
      }`}
    >
      <div className="bg-dark-lighter border border-gray-800 rounded-2xl shadow-2xl shadow-primary/10 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary/20 to-cyan-500/20 px-4 py-3 flex items-center justify-between border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Bot className="w-6 h-6 text-primary" />
              <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border border-dark ${
                aiStatus?.available ? 'bg-green-500' : 'bg-yellow-500'
              }`} />
            </div>
            <div>
              <h3 className="font-semibold text-white text-sm">HybridX AI</h3>
              <p className="text-xs text-gray-400">
                {aiStatus?.available ? 'Ollama Active' : 'Basic Mode'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            >
              {isMinimized ? (
                <Maximize2 className="w-4 h-4 text-gray-400" />
              ) : (
                <Minimize2 className="w-4 h-4 text-gray-400" />
              )}
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Messages */}
            <div className="h-80 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-gray-700">
              {messages.length === 0 && showQuickActions && (
                <div className="space-y-4">
                  <div className="text-center">
                    <Sparkles className="w-10 h-10 text-primary mx-auto mb-2" />
                    <p className="text-white font-medium">Hi! I'm your AI trading assistant</p>
                    <p className="text-gray-400 text-sm mt-1">
                      I can help with strategies, analysis, and building workflows.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    {quickActions.map((action, i) => (
                      <button
                        key={i}
                        onClick={action.action}
                        className="w-full flex items-center gap-3 p-3 bg-dark rounded-xl border border-gray-800 hover:border-primary/50 transition-colors group"
                      >
                        <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                          <action.icon className="w-4 h-4 text-primary" />
                        </div>
                        <span className="text-sm text-gray-300">{action.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((message, i) => (
                <div
                  key={i}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl ${
                      message.role === 'user'
                        ? 'bg-primary text-black rounded-br-md'
                        : 'bg-dark border border-gray-800 text-gray-200 rounded-bl-md'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    <p className={`text-xs mt-1 ${
                      message.role === 'user' ? 'text-black/60' : 'text-gray-500'
                    }`}>
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}

              {chatMutation.isPending && (
                <div className="flex justify-start">
                  <div className="bg-dark border border-gray-800 px-4 py-3 rounded-2xl rounded-bl-md">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 text-primary animate-spin" />
                      <span className="text-sm text-gray-400">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-800">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask me anything..."
                  className="flex-1 bg-dark border border-gray-800 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-primary/50"
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || chatMutation.isPending}
                  className="p-2.5 bg-primary rounded-xl hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="w-4 h-4 text-black" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">
                AI can make mistakes. Always verify important information.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AIAssistant;
